import React, { useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "../../../supabaseClient";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const cardStyle: React.CSSProperties = {
  width: "100%",
  maxWidth: 460,
  background: "#ffffff",
  border: "1px solid #e5e7eb",
  borderRadius: 20,
  boxShadow: "0 24px 60px rgba(15, 23, 42, 0.08)",
  padding: "2rem",
};

const labelStyle: React.CSSProperties = {
  display: "block",
  marginBottom: "0.45rem",
  fontSize: "0.95rem",
  fontWeight: 600,
  color: "#0f172a",
};

const inputStyle: React.CSSProperties = {
  width: "100%",
  padding: "0.9rem 1rem",
  borderRadius: 12,
  border: "1px solid #cbd5e1",
  background: "#ffffff",
  color: "#0f172a",
  fontSize: "0.95rem",
  outline: "none",
  boxSizing: "border-box",
};

const errorTextStyle: React.CSSProperties = {
  color: "#b91c1c",
  fontSize: "0.82rem",
  marginTop: "0.45rem",
};

const ForgotPasswordPage: React.FC = () => {
  const [email, setEmail] = useState("");
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [formMessage, setFormMessage] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const validate = () => {
    if (!email.trim()) {
      setFieldError("Email is required.");
      return false;
    }

    if (!EMAIL_REGEX.test(email.trim())) {
      setFieldError("Enter a valid email address.");
      return false;
    }

    setFieldError(null);
    return true;
  };

  const handleSubmit: React.FormEventHandler<HTMLFormElement> = async (e) => {
    e.preventDefault();
    setFormError(null);
    setFormMessage(null);

    if (!validate()) return;

    try {
      setSubmitting(true);

      const { error } = await supabase.auth.resetPasswordForEmail(email.trim(), {
        redirectTo: `${window.location.origin}/reset-password`,
      });

      if (error) {
        setFormError(error.message);
        return;
      }

      setFormMessage(
        "If an account exists for that email, a password reset link has been sent.",
      );
    } catch (err) {
      console.error("Forgot password error", err);
      setFormError("Something went wrong. Try again in a moment.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main
      style={{
        minHeight: "calc(100vh - 160px)",
        background:
          "linear-gradient(180deg, #f8fafc 0%, #ffffff 40%, #f8fafc 100%)",
        padding: "3rem 1.25rem 4rem",
      }}
    >
      <div
        style={{
          maxWidth: 1100,
          margin: "0 auto",
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
          gap: "2rem",
          alignItems: "center",
        }}
      >
        <section>
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "0.5rem",
              padding: "0.45rem 0.8rem",
              borderRadius: 999,
              background: "#e2e8f0",
              color: "#334155",
              fontSize: "0.82rem",
              fontWeight: 700,
              letterSpacing: "0.01em",
              marginBottom: "1rem",
            }}
          >
            HnB account recovery
          </div>

          <h1
            style={{
              margin: 0,
              fontSize: "clamp(2rem, 4vw, 3.2rem)",
              lineHeight: 1.05,
              letterSpacing: "-0.03em",
              color: "#0f172a",
            }}
          >
            Reset your password
          </h1>

          <p
            style={{
              marginTop: "1rem",
              maxWidth: 560,
              fontSize: "1.02rem",
              lineHeight: 1.75,
              color: "#475569",
            }}
          >
            Enter the email linked to your customer account and we will send you a
            secure password reset link.
          </p>

          <div
            style={{
              marginTop: "1.5rem",
              display: "grid",
              gap: "0.9rem",
              color: "#334155",
              fontSize: "0.95rem",
            }}
          >
            <div>• Use the same email you use to sign in.</div>
            <div>• Password reset links are delivered by email.</div>
            <div>• Provider and internal account flows should remain role-separated.</div>
          </div>
        </section>

        <section>
          <div style={cardStyle}>
            <h2
              style={{
                margin: 0,
                fontSize: "1.5rem",
                color: "#0f172a",
                letterSpacing: "-0.02em",
              }}
            >
              Forgot password
            </h2>

            <p
              style={{
                marginTop: "0.6rem",
                marginBottom: "1.5rem",
                color: "#64748b",
                lineHeight: 1.6,
                fontSize: "0.95rem",
              }}
            >
              We will email you a link to create a new password.
            </p>

            <form onSubmit={handleSubmit} noValidate>
              <div style={{ marginBottom: "1rem" }}>
                <label htmlFor="email" style={labelStyle}>
                  Email
                </label>
                <input
                  id="email"
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (fieldError) setFieldError(null);
                    if (formError) setFormError(null);
                    if (formMessage) setFormMessage(null);
                  }}
                  style={inputStyle}
                  aria-invalid={fieldError ? "true" : "false"}
                />
                {fieldError && <p style={errorTextStyle}>{fieldError}</p>}
              </div>

              {formError && (
                <div
                  style={{
                    marginBottom: "1rem",
                    padding: "0.85rem 1rem",
                    borderRadius: 12,
                    background: "#fef2f2",
                    border: "1px solid #fecaca",
                    color: "#991b1b",
                    fontSize: "0.92rem",
                  }}
                >
                  {formError}
                </div>
              )}

              {formMessage && (
                <div
                  style={{
                    marginBottom: "1rem",
                    padding: "0.85rem 1rem",
                    borderRadius: 12,
                    background: "#ecfdf5",
                    border: "1px solid #a7f3d0",
                    color: "#065f46",
                    fontSize: "0.92rem",
                    lineHeight: 1.6,
                  }}
                >
                  {formMessage}
                </div>
              )}

              <button
                type="submit"
                disabled={submitting}
                style={{
                  width: "100%",
                  border: "none",
                  borderRadius: 999,
                  padding: "0.95rem 1rem",
                  background: submitting ? "#475569" : "#111827",
                  color: "#ffffff",
                  fontSize: "0.96rem",
                  fontWeight: 700,
                  cursor: submitting ? "not-allowed" : "pointer",
                  boxShadow: "0 14px 30px rgba(15, 23, 42, 0.14)",
                }}
              >
                {submitting ? "Sending link..." : "Send reset link"}
              </button>
            </form>

            <p
              style={{
                marginTop: "1rem",
                marginBottom: 0,
                fontSize: "0.92rem",
                color: "#64748b",
                lineHeight: 1.6,
              }}
            >
              Remembered your password?{" "}
              <Link
                to="/login"
                style={{
                  color: "#0f172a",
                  fontWeight: 600,
                  textDecoration: "none",
                }}
              >
                Back to login
              </Link>
            </p>
          </div>
        </section>
      </div>
    </main>
  );
};

export default ForgotPasswordPage;